// ─── ChatDrawer — chat operador ↔ motoristas (multi-conversa) ────────────
//
// Substitui o antigo ChatDriverDialog (modal). Layout em drawer lateral
// fixo à direita do ecrã com 2 colunas:
//   · Esquerda — lista de threads (motoristas com mensagens recentes),
//     ordenadas por última mensagem. Mostra OT activa, contagem
//     não-lidas, tempo desde última mensagem.
//   · Direita — conversa do motorista seleccionado, com header
//     contextual (OT activa, GPS ao vivo, telefone), thread scrollável
//     e caixa de envio.
//
// O drawer é controlado pelo Header (props `open` + `initialDriverId`).
// Quando recebe `initialDriverId`, abre directamente nessa thread; senão
// abre na primeira thread com mensagens não-lidas (ou na mais recente).
//
// WebSocket: escuta `message:new` para refrescar lista E thread activa
// sem polling. A lista é re-fetched a cada abertura + ao receber msg.

import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import {
  Box, Typography, IconButton, Button, TextField, Chip, CircularProgress, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import SearchIcon from '@material-ui/icons/Search';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PhoneIcon from '@material-ui/icons/Phone';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Menu, MenuItem } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../App';
import { getSocket } from '../../utils/websocket';

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1290,
    background: 'rgba(15,23,42,0.35)', backdropFilter: 'blur(2px)',
  },
  drawer: {
    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1291,
    width: 760, maxWidth: '96vw',
    background: '#fff',
    boxShadow: '-12px 0 32px rgba(15,23,42,0.18)',
    display: 'flex', flexDirection: 'column',
    transform: 'translateX(0)',
    transition: 'transform 0.25s ease',
  },
  drawerClosed: {
    transform: 'translateX(100%)',
  },
  topbar: {
    flex: '0 0 auto',
    padding: '12px 16px',
    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    borderBottom: '1px solid #c7d2fe',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  topbarIcon: {
    width: 34, height: 34, borderRadius: 9,
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(99,102,241,0.3)',
  },
  body: {
    flex: 1, minHeight: 0, display: 'grid',
    gridTemplateColumns: '260px 1fr',
  },
  list: {
    borderRight: '1px solid #e2e8f0',
    background: '#f8fafc',
    display: 'flex', flexDirection: 'column', minWidth: 0,
  },
  listSearch: {
    padding: '10px 12px',
    borderBottom: '1px solid #e2e8f0',
    background: '#fff',
  },
  listInner: { flex: 1, minHeight: 0, overflowY: 'auto' },
  threadCard: {
    padding: '10px 12px',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    display: 'flex', flexDirection: 'column', gap: 3,
    transition: 'background 0.12s',
    '&:hover': { background: '#f1f5f9' },
  },
  threadCardActive: {
    background: '#e0e7ff !important',
    borderLeft: '3px solid #4f46e5',
    paddingLeft: 9,
  },
  avatar: {
    width: 28, height: 28, borderRadius: '50%',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.66rem', fontWeight: 800, color: '#fff',
    flexShrink: 0,
  },
  threadHeader: {
    flex: '0 0 auto',
    padding: '12px 16px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  thread: {
    flex: 1, minHeight: 0, overflowY: 'auto',
    padding: '12px 14px',
    background: '#f8fafc',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  bubble: {
    maxWidth: '78%',
    padding: '8px 12px',
    borderRadius: 14,
    fontSize: '0.78rem', lineHeight: 1.4,
    wordBreak: 'break-word',
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
  },
  bubbleAdmin: {
    alignSelf: 'flex-end',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#fff',
    borderBottomRightRadius: 4,
  },
  bubbleDriver: {
    alignSelf: 'flex-start',
    background: '#fff',
    color: '#0f172a',
    border: '1px solid #e2e8f0',
    borderBottomLeftRadius: 4,
  },
  meta: { fontSize: '0.56rem', color: '#94a3b8', marginTop: 2 },
  metaAdmin: { textAlign: 'right', color: '#dbeafe' },
  inputRow: {
    flex: '0 0 auto',
    padding: '10px 14px',
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'flex-end', gap: 8,
  },
  input: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12, fontSize: '0.8rem', background: '#f8fafc',
    },
  },
  sendBtn: {
    minWidth: 0, padding: '10px 14px',
    borderRadius: 12, textTransform: 'none', fontWeight: 700,
    fontSize: '0.78rem',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#fff',
    '&:disabled': { background: '#e2e8f0', color: '#94a3b8' },
  },
  emptyState: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 8,
    color: '#94a3b8', textAlign: 'center', padding: 32,
  },
}));

function fmtTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  const today = new Date();
  const sameDay = dt.toDateString() === today.toDateString();
  return sameDay
    ? dt.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
    : dt.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function fmtAgo(iso) {
  if (!iso) return '';
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

// Cor estável derivada do nome — garante que cada motorista tem o seu
// avatar consistente mesmo quando vários têm o mesmo primeiro nome.
function colorFromName(name) {
  if (!name) return '#64748b';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const palette = ['#0284c7', '#0891b2', '#0d9488', '#16a34a', '#65a30d', '#ca8a04', '#dc2626', '#db2777', '#a21caf', '#7c3aed', '#4f46e5'];
  return palette[Math.abs(hash) % palette.length];
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ChatDrawer({ open, onClose, initialDriverId, initialDriverName, initialDriverPhone, initialActiveOT }) {
  const classes = useStyles();
  const { token, clientSettings } = useContext(AuthContext);
  const quickReplies = useMemo(() => (
    Array.isArray(clientSettings?.chatQuickReplies) && clientSettings.chatQuickReplies.length > 0
      ? clientSettings.chatQuickReplies
      : ['Recebido ✓', 'Cheguei OK?', 'Posso ajudar?', 'Reportar quando concluir']
  ), [clientSettings?.chatQuickReplies]);

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'on_route'
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'unread' | 'name'
  // Presença online — Set<driverId> dos motoristas com WS activo. Vem
  // do snapshot ao conectar + actualizações driver:presence em runtime.
  const [onlineSet, setOnlineSet] = useState(new Set());
  // Menu kebab do header da conversa (marcar não-lida, etc.)
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Thread activa — carregada quando activeId muda
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  const vehicles = useSelector(s => s.vehicles?.list || []);

  const fetchThreads = useCallback(async () => {
    if (!token) return;
    setThreadsLoading(true);
    try {
      const r = await fetch('/planner-api/messages/threads', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setThreads(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setThreadsLoading(false); }
  }, [token]);

  // Boot — quando o drawer abre, carrega threads e selecciona a inicial.
  useEffect(() => {
    if (!open) return;
    fetchThreads();
    if (initialDriverId) {
      setActiveId(Number(initialDriverId));
    }
  }, [open, initialDriverId, fetchThreads]);

  // Se não veio driverId inicial, escolhe a primeira thread após o fetch.
  useEffect(() => {
    if (!open) return;
    if (activeId != null) return;
    if (threads.length === 0) return;
    const firstUnread = threads.find(t => t.unreadCount > 0);
    setActiveId((firstUnread || threads[0]).driverId);
  }, [open, activeId, threads]);

  // Reset ao fechar
  useEffect(() => {
    if (open) return;
    setActiveId(null);
    setMessages([]);
    setInput('');
    setSearch('');
    setFilter('all');
  }, [open]);

  const activeThread = useMemo(() => {
    if (activeId == null) return null;
    const fromList = threads.find(t => t.driverId === activeId);
    if (fromList) return fromList;
    // Fallback: usar dados iniciais do bell (caso a thread ainda não
    // tenha sido fetched ou só haja drafts).
    if (initialDriverId && Number(initialDriverId) === Number(activeId)) {
      return {
        driverId: Number(activeId),
        driverName: initialDriverName,
        driverPhone: initialDriverPhone,
        activeOT: initialActiveOT,
        unreadCount: 0,
        lastMessage: null,
      };
    }
    return null;
  }, [activeId, threads, initialDriverId, initialDriverName, initialDriverPhone, initialActiveOT]);

  const scrollToBottom = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, []);

  // Carrega messages quando muda activeId.
  useEffect(() => {
    if (!open || !activeId || !token) {
      setMessages([]);
      return;
    }
    setMsgLoading(true);
    fetch(`/planner-api/messages/${activeId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { messages: [] })
      .then(data => {
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
        scrollToBottom();
      })
      .catch(() => setMessages([]))
      .finally(() => setMsgLoading(false));
    // Marca driver→admin messages como lidas
    fetch(`/planner-api/messages/${activeId}/read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).then(() => {
      // Local update: zera unreadCount desta thread
      setThreads(prev => prev.map(t => t.driverId === activeId ? { ...t, unreadCount: 0 } : t));
    }).catch(() => {});
  }, [open, activeId, token, scrollToBottom]);

  // WS listeners — mensagens + presença.
  useEffect(() => {
    const sock = getSocket();
    if (!sock) return;
    const onPresence = ({ driverId, online }) => {
      setOnlineSet(prev => {
        const next = new Set(prev);
        if (online) next.add(Number(driverId)); else next.delete(Number(driverId));
        return next;
      });
    };
    const onSnapshot = ({ onlineDriverIds }) => {
      setOnlineSet(new Set((onlineDriverIds || []).map(Number)));
    };
    sock.on('driver:presence', onPresence);
    sock.on('driver:presence:snapshot', onSnapshot);
    return () => {
      sock.off('driver:presence', onPresence);
      sock.off('driver:presence:snapshot', onSnapshot);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const sock = getSocket();
    if (!sock) return;
    const onNew = (msg) => {
      // Update thread activa
      if (Number(msg.driverId) === Number(activeId)) {
        setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        scrollToBottom();
        // Se for do motorista, marcar como lida imediatamente
        if (!msg.fromAdmin && token) {
          fetch(`/planner-api/messages/${activeId}/read`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          }).catch(() => {});
        }
      }
      // Update lista — incrementa unreadCount e movimenta para o topo
      setThreads(prev => {
        const idx = prev.findIndex(t => t.driverId === msg.driverId);
        if (idx < 0) {
          // Thread nova — refrescar lista
          fetchThreads();
          return prev;
        }
        const updated = { ...prev[idx] };
        updated.lastMessage = {
          id: msg.id, body: msg.body, createdAt: msg.createdAt, fromAdmin: msg.fromAdmin,
        };
        if (!msg.fromAdmin && Number(msg.driverId) !== Number(activeId)) {
          updated.unreadCount = (updated.unreadCount || 0) + 1;
        }
        const next = [...prev];
        next.splice(idx, 1);
        next.unshift(updated);
        return next;
      });
    };
    sock.on('message:new', onNew);
    return () => { sock.off('message:new', onNew); };
  }, [open, activeId, token, scrollToBottom, fetchThreads]);

  const filteredThreads = useMemo(() => {
    let out = threads;
    if (filter === 'unread') out = out.filter(t => t.unreadCount > 0);
    if (filter === 'on_route') out = out.filter(t => !!t.activeOT);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(t =>
        (t.driverName || '').toLowerCase().includes(q)
        || (t.activeOT?.vehiclePlate || '').toLowerCase().includes(q)
        || (t.activeOT?.orderNumber || '').toLowerCase().includes(q)
      );
    }
    // Ordenação configurável — recent (last msg desc), unread primeiro,
    // alfabético por nome.
    if (sortBy === 'unread') {
      out = [...out].sort((a, b) => {
        if ((b.unreadCount || 0) !== (a.unreadCount || 0)) return (b.unreadCount || 0) - (a.unreadCount || 0);
        return new Date(b.lastMessage?.createdAt || 0) - new Date(a.lastMessage?.createdAt || 0);
      });
    } else if (sortBy === 'name') {
      out = [...out].sort((a, b) => (a.driverName || '').localeCompare(b.driverName || '', 'pt'));
    } else {
      out = [...out].sort((a, b) => new Date(b.lastMessage?.createdAt || 0) - new Date(a.lastMessage?.createdAt || 0));
    }
    return out;
  }, [threads, filter, search, sortBy]);

  // Veículo da thread activa (para GPS + matrícula)
  const vehicleMatch = useMemo(() => {
    if (!activeThread) return null;
    const ot = activeThread.activeOT;
    if (ot?.vehicleId) {
      const byId = vehicles.find(v => Number(v.id) === Number(ot.vehicleId));
      if (byId) return byId;
    }
    if (ot?.vehiclePlate) {
      const byPlate = vehicles.find(v => (v.plate || '').toUpperCase() === String(ot.vehiclePlate).toUpperCase());
      if (byPlate) return byPlate;
    }
    return vehicles.find(v => Number(v.driverId) === Number(activeThread.driverId)) || null;
  }, [activeThread, vehicles]);
  const livePos = vehicleMatch?.position;
  const fixAgeMin = livePos?.fixTime
    ? Math.max(0, Math.floor((Date.now() - new Date(livePos.fixTime).getTime()) / 60000))
    : null;

  async function markUnread() {
    if (!activeId || !token) return;
    setMenuAnchor(null);
    try {
      await fetch(`/planner-api/messages/${activeId}/unread`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setThreads(prev => prev.map(t => t.driverId === activeId
        ? { ...t, unreadCount: Math.max(1, t.unreadCount || 0) }
        : t));
    } catch (e) { /* ignore */ }
  }

  // Insere o template no input. Operador pode editar antes de enviar
  // (alternativa a enviar directo — mais profissional, evita clicks
  // acidentais a despachar mensagens sem rever).
  function applyQuickReply(text) {
    setInput(prev => (prev ? prev + ' ' : '') + text);
  }

  async function handleSend() {
    const body = input.trim();
    if (!body || sending || !activeId) return;
    setSending(true);
    try {
      const res = await fetch(`/planner-api/messages/${activeId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error('falha');
      const created = await res.json();
      setMessages(prev => prev.some(m => m.id === created.id) ? prev : [...prev, created]);
      setInput('');
      scrollToBottom();
      // Update lista — actualiza last msg
      setThreads(prev => prev.map(t => t.driverId === activeId
        ? { ...t, lastMessage: { id: created.id, body: created.body, createdAt: created.createdAt, fromAdmin: true } }
        : t));
    } catch (e) {
      console.error('send message error:', e);
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // ESC fecha o drawer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <Box className={classes.overlay} onClick={onClose} />
      <Box className={classes.drawer}>
        {/* Topbar */}
        <Box className={classes.topbar}>
          <Box className={classes.topbarIcon}>
            <ChatBubbleOutlineIcon style={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography style={{ fontSize: '0.9rem', fontWeight: 800, color: '#1e293b' }}>
              Chat com motoristas
            </Typography>
            <Typography style={{ fontSize: '0.66rem', color: '#475569' }}>
              {threads.length} {threads.length === 1 ? 'conversa activa' : 'conversas activas'}
              {threads.some(t => t.unreadCount > 0) && ` · ${threads.reduce((a, t) => a + (t.unreadCount || 0), 0)} não lidas`}
            </Typography>
          </Box>
          <Tooltip title="Fechar (Esc)">
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Tooltip>
        </Box>

        {/* Body 2 colunas */}
        <Box className={classes.body}>
          {/* Coluna esquerda — lista */}
          <Box className={classes.list}>
            <Box className={classes.listSearch}>
              <TextField
                fullWidth size="small" variant="outlined"
                placeholder="Procurar motorista, OT, matrícula…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon style={{ fontSize: 16, color: '#94a3b8', marginRight: 6 }} />,
                  style: { fontSize: '0.76rem', background: '#f8fafc' },
                }}
              />
              <Box style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                {[
                  { key: 'all', label: 'Todas' },
                  { key: 'unread', label: 'Não lidas' },
                  { key: 'on_route', label: 'Em rota' },
                ].map(f => (
                  <Chip
                    key={f.key}
                    label={f.label}
                    size="small"
                    onClick={() => setFilter(f.key)}
                    style={{
                      height: 22, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer',
                      background: filter === f.key ? '#4f46e5' : '#f1f5f9',
                      color: filter === f.key ? '#fff' : '#475569',
                    }}
                  />
                ))}
              </Box>
              <Box style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Ordenar:
                </Typography>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  style={{ flex: 1, fontSize: '0.66rem', padding: '3px 6px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc' }}
                >
                  <option value="recent">Mais recente</option>
                  <option value="unread">Não lidas primeiro</option>
                  <option value="name">Alfabético</option>
                </select>
              </Box>
            </Box>
            <Box className={classes.listInner}>
              {threadsLoading && threads.length === 0 ? (
                <Box style={{ padding: 16, textAlign: 'center' }}>
                  <CircularProgress size={20} style={{ color: '#6366f1' }} />
                </Box>
              ) : filteredThreads.length === 0 ? (
                <Box style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.74rem' }}>
                  Sem conversas{search || filter !== 'all' ? ' (filtros aplicados)' : ''}.
                </Box>
              ) : filteredThreads.map(t => (
                <Box
                  key={t.driverId}
                  className={`${classes.threadCard} ${t.driverId === activeId ? classes.threadCardActive : ''}`}
                  onClick={() => setActiveId(t.driverId)}
                >
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Box style={{ position: 'relative', flexShrink: 0 }}>
                      <Box className={classes.avatar} style={{ background: colorFromName(t.driverName) }}>
                        {initials(t.driverName)}
                      </Box>
                      <Box style={{
                        position: 'absolute', bottom: -1, right: -1,
                        width: 9, height: 9, borderRadius: '50%',
                        background: onlineSet.has(Number(t.driverId)) ? '#16a34a' : '#94a3b8',
                        border: '2px solid #f8fafc',
                      }} />
                    </Box>
                    <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.driverName}
                    </Typography>
                    {t.unreadCount > 0 && (
                      <Chip label={t.unreadCount} size="small"
                        style={{ height: 18, fontSize: '0.55rem', fontWeight: 800, background: '#dc2626', color: '#fff' }} />
                    )}
                  </Box>
                  <Typography style={{ fontSize: '0.68rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.lastMessage?.fromAdmin ? '↪ ' : ''}{t.lastMessage?.body || '—'}
                  </Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.58rem', color: '#94a3b8' }}>
                    {t.activeOT && (
                      <>
                        <AssignmentIcon style={{ fontSize: 11 }} />
                        <Typography style={{ fontSize: '0.58rem', color: '#3b82f6', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {t.activeOT.orderNumber || `OT #${t.activeOT.id}`}
                        </Typography>
                      </>
                    )}
                    <span style={{ marginLeft: 'auto' }}>{fmtAgo(t.lastMessage?.createdAt)}</span>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Coluna direita — thread */}
          {activeThread ? (
            <Box style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
              <Box className={classes.threadHeader}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Box style={{ position: 'relative' }}>
                    <Box className={classes.avatar} style={{ width: 40, height: 40, fontSize: '0.86rem', background: colorFromName(activeThread.driverName) }}>
                      {initials(activeThread.driverName)}
                    </Box>
                    <Box style={{
                      position: 'absolute', bottom: -1, right: -1,
                      width: 12, height: 12, borderRadius: '50%',
                      background: onlineSet.has(Number(activeThread.driverId)) ? '#16a34a' : '#94a3b8',
                      border: '2px solid #fff',
                    }} />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
                        {activeThread.driverName}
                      </Typography>
                      <Typography style={{ fontSize: '0.62rem', color: onlineSet.has(Number(activeThread.driverId)) ? '#16a34a' : '#94a3b8', fontWeight: 700 }}>
                        · {onlineSet.has(Number(activeThread.driverId)) ? 'online' : 'offline'}
                      </Typography>
                    </Box>
                    <Box style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                      {activeThread.driverPhone && (
                        <Chip
                          size="small"
                          icon={<PhoneIcon style={{ fontSize: 12 }} />}
                          label={activeThread.driverPhone}
                          clickable
                          component="a"
                          href={`tel:${activeThread.driverPhone}`}
                          style={{ height: 22, fontSize: '0.64rem', background: '#dbeafe', color: '#1d4ed8', textDecoration: 'none' }}
                        />
                      )}
                      {activeThread.activeOT ? (
                        <Chip
                          size="small"
                          icon={<AssignmentIcon style={{ fontSize: 12 }} />}
                          label={`${activeThread.activeOT.orderNumber || `OT #${activeThread.activeOT.id}`} · ${activeThread.activeOT.deliveryName || 'em curso'}`}
                          style={{ height: 22, fontSize: '0.64rem', background: '#dbeafe', color: '#1e40af', fontWeight: 600, maxWidth: 280 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<AssignmentIcon style={{ fontSize: 12 }} />}
                          label="Sem OT em trânsito"
                          style={{ height: 22, fontSize: '0.64rem', background: '#f1f5f9', color: '#64748b' }}
                        />
                      )}
                      {activeThread.activeOT?.vehiclePlate && (
                        <Chip
                          size="small"
                          icon={<LocalShippingIcon style={{ fontSize: 12 }} />}
                          label={activeThread.activeOT.vehiclePlate}
                          style={{ height: 22, fontSize: '0.64rem', background: '#fef3c7', color: '#92400e', fontWeight: 700 }}
                        />
                      )}
                      {livePos?.latitude != null && livePos?.longitude != null ? (
                        <Chip
                          size="small"
                          clickable
                          onClick={() => window.open(`https://www.google.com/maps?q=${livePos.latitude},${livePos.longitude}`, '_blank', 'noopener')}
                          icon={<LocationOnIcon style={{ fontSize: 12 }} />}
                          label={`GPS${fixAgeMin != null ? ` · há ${fixAgeMin}m` : ''}`}
                          style={{ height: 22, fontSize: '0.62rem', background: '#dcfce7', color: '#166534', fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<LocationOnIcon style={{ fontSize: 12 }} />}
                          label="Sem GPS"
                          style={{ height: 22, fontSize: '0.62rem', background: '#f1f5f9', color: '#94a3b8' }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Tooltip title="Opções da conversa">
                    <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                      <MoreVertIcon style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={menuAnchor}
                    open={!!menuAnchor}
                    onClose={() => setMenuAnchor(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem onClick={markUnread} style={{ fontSize: '0.78rem', gap: 8 }}>
                      <ChatBubbleOutlineIcon style={{ fontSize: 16, color: '#dc2626' }} />
                      Marcar como não-lida
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>

              <Box ref={threadRef} className={classes.thread}>
                {msgLoading && messages.length === 0 ? (
                  <Box style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                    <CircularProgress size={22} style={{ color: '#6366f1' }} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box className={classes.emptyState}>
                    <ChatBubbleOutlineIcon style={{ fontSize: 36, color: '#cbd5e1' }} />
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                      Sem mensagens nesta conversa — escreve a primeira.
                    </Typography>
                  </Box>
                ) : (
                  messages.map(m => (
                    <Box key={m.id} style={{ display: 'flex', flexDirection: 'column' }}>
                      <Box className={`${classes.bubble} ${m.fromAdmin ? classes.bubbleAdmin : classes.bubbleDriver}`}>
                        {m.body}
                      </Box>
                      <Typography className={`${classes.meta} ${m.fromAdmin ? classes.metaAdmin : ''}`}>
                        {m.fromAdmin ? (m.senderUserName || 'Operador') : (activeThread.driverName || 'Motorista')} · {fmtTime(m.createdAt)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>

              {/* Respostas rápidas — atalhos configuráveis pelo tenant.
                  Inserem texto no input em vez de enviar directamente, para
                  o operador poder rever/editar antes de despachar. */}
              {quickReplies.length > 0 && (
                <Box style={{ flex: '0 0 auto', padding: '6px 14px 0 14px', background: '#fff', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {quickReplies.map((qr, i) => (
                    <Chip
                      key={`qr-${i}`}
                      label={qr}
                      size="small"
                      onClick={() => applyQuickReply(qr)}
                      style={{ height: 22, fontSize: '0.66rem', fontWeight: 600, cursor: 'pointer', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                    />
                  ))}
                </Box>
              )}

              <Box className={classes.inputRow}>
                <TextField
                  className={classes.input}
                  multiline minRows={1} maxRows={4}
                  size="small" variant="outlined"
                  placeholder="Escrever mensagem… (Enter para enviar, Shift+Enter para nova linha)"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={sending}
                />
                <Button
                  className={classes.sendBtn}
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  startIcon={sending ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <SendIcon style={{ fontSize: 16 }} />}
                >
                  Enviar
                </Button>
              </Box>
            </Box>
          ) : (
            <Box className={classes.emptyState}>
              <ChatBubbleOutlineIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
              <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: '#475569' }}>
                Escolhe uma conversa
              </Typography>
              <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                A lista à esquerda mostra todos os motoristas com mensagens recentes.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
